import DialogContainer from "../../DialogContainer/DialogContainer";
import { useEffect } from "react";

type ErrorType = "error" | "warning" | "info";

interface ErrorDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  message: string | null;
  title?: string;
  type?: ErrorType;
  buttonText?: string;
  autoClose?: boolean;
  autoCloseTime?: number;
}

/**
 * ErrorDialog component for displaying error messages in a modal dialog
 *
 * @param isOpen - Boolean to control dialog visibility
 * @param setIsOpen - Function to set dialog visibility
 * @param message - Error message to display
 * @param title - Optional custom title (defaults based on type)
 * @param type - Type of error: 'error', 'warning', or 'info' (defaults to 'error')
 * @param buttonText - Custom button text (defaults to "Close")
 * @param autoClose - Whether to automatically close the dialog after a delay (defaults to false)
 * @param autoCloseTime - Time in milliseconds before auto-closing (defaults to 5000ms)
 */
const ErrorDialog = ({
  isOpen,
  setIsOpen,
  message,
  title,
  type = "error",
  buttonText = "Close",
  autoClose = false,
  autoCloseTime = 5000,
}: ErrorDialogProps) => {
  // Auto-close functionality
  useEffect(() => {
    if (isOpen && autoClose) {
      const timer = setTimeout(() => {
        setIsOpen(false);
      }, autoCloseTime);

      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, autoCloseTime, setIsOpen]);

  // Define styles and content based on error type
  const config = {
    error: {
      icon: (
        <svg
          className="h-8 w-8 text-red-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
      bgColor: "bg-red-100",
      textColor: "text-red-500",
      buttonColor: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
      defaultTitle: "Error",
    },
    warning: {
      icon: (
        <svg
          className="h-8 w-8 text-yellow-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-500",
      buttonColor: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
      defaultTitle: "Warning",
    },
    info: {
      icon: (
        <svg
          className="h-8 w-8 text-blue-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      bgColor: "bg-blue-100",
      textColor: "text-blue-500",
      buttonColor: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
      defaultTitle: "Information",
    },
  };

  const { icon, bgColor, textColor, buttonColor, defaultTitle } = config[type];
  const displayTitle = title || defaultTitle;

  // Handle keyboard events for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  if (!message) return null;

  return (
    <DialogContainer
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onClose={() => setIsOpen(false)}
    >
      <div
        className="bg-white p-12 dark:bg-neutral-800"
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="dialog"
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <div className="flex flex-col items-center justify-center">
          <div
            className={`mb-4 flex h-16 w-16 items-center justify-center rounded-full ${bgColor}`}
          >
            {icon}
          </div>
          <h3
            id="error-dialog-title"
            className="mb-2 text-lg font-medium text-gray-900 dark:text-white"
          >
            {displayTitle}
          </h3>
          <p
            id="error-dialog-description"
            className="mb-6 text-center text-gray-600 dark:text-gray-300"
          >
            {message}
          </p>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className={`inline-flex items-center justify-center rounded-md border border-transparent ${buttonColor} px-4 py-2 text-sm font-medium text-white shadow-xs focus:outline-hidden focus:ring-2 focus:ring-offset-2`}
            aria-label={`Close ${type} dialog`}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </DialogContainer>
  );
};

export default ErrorDialog;
